import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import PageTitle from "./PageTitle";
import Spacer from "./Spacer";
import "./DemographicsPage.styles.css";
import { getParticipantIdFromSearchParams } from '../utils/urlParams';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

type UsdEarningsSummary = {
  totalUsd: number;
  roundUsd?: number[];
};

const GameCompletionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const participantId = useMemo(() => getParticipantIdFromSearchParams(location.search), [location.search]);
  const [usdEarnings, setUsdEarnings] = useState<UsdEarningsSummary | null>(null);

  const handleContinue = () => {
    // Preserve all URL parameters
    const queryParams = new URLSearchParams(location.search);
    const newUrl = '/post-game-questions?' + queryParams.toString();
    console.log("GameCompletionPage: Navigating to:", newUrl);
    navigate(newUrl);
  };

  useEffect(() => {
    if (!API_BASE_URL || !participantId) return;

    let cancelled = false;
    fetch(`${API_BASE_URL}/checkPlayerStatus`, {
      method: 'POST',
      headers: { 'prolific-id': participantId },
    })
      .then((res) => res.json().then((payload) => ({ ok: res.ok, data: payload })))
      .then(({ ok, data }) => {
        if (cancelled || !ok) return;
        const earnings = data?.usdEarnings;
        const totalUsd = typeof earnings?.totalUsd === 'number' ? earnings.totalUsd : 0;
        setUsdEarnings({ totalUsd, roundUsd: earnings?.roundUsd });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [participantId]);

  return (
    <div className="demographics-bg">
      <div className="demographics-card fade-in">
        <PageTitle title="Congratulations!" />
        <Spacer />
        
        {/* Main explanation */}
        <div className="demographics-thankyou" style={{ fontSize: 20, marginBottom: 18 }}>
          You have successfully completed the game! 🎉<br />
          Thank you for your participation and excellent work.
        </div>

        {/* Success message box */}
        <div style={{
          background: '#e8f5e9',
          border: '1.5px solid #4caf50',
          borderRadius: 12,
          padding: '22px 18px',
          margin: '0 auto 18px auto',
          maxWidth: 480,
          textAlign: 'center',
          boxShadow: '0 2px 12px rgba(76, 175, 80, 0.08)',
          fontSize: 18,
          color: '#2e7d32',
          fontWeight: 600
        }}>
          <div style={{ marginBottom: 12 }}>
            <span role="img" aria-label="checkmark" style={{ fontSize: 24, marginRight: 8 }}>✅</span>
            Game Completed Successfully
          </div>
          <div style={{ fontSize: 16, color: '#388e3c', fontWeight: 400 }}>
            You have finished the discussion and the one investment round.
          </div>
        </div>

        {usdEarnings && usdEarnings.totalUsd > 0 && (
          <div style={{
            background: '#eef6ff',
            border: '1.5px solid #60a5fa',
            borderRadius: 12,
            padding: '18px 16px',
            margin: '0 auto 18px auto',
            maxWidth: 480,
            width: '100%',
            boxSizing: 'border-box',
            boxShadow: '0 2px 12px rgba(59, 130, 246, 0.08)',
            textAlign: 'center',
          }}>
            <div style={{ fontWeight: 800, color: '#0b4aa2', marginBottom: 8 }}>
              Total study bonus
            </div>
            <div style={{ fontWeight: 900, fontSize: 28, color: '#0b4aa2' }}>
              ${usdEarnings.totalUsd.toFixed(2)}
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 10, lineHeight: 1.45 }}>
              Converted from coins earned in the study.
            </div>
          </div>
        )}

        {/* Next steps information */}
        <div style={{ 
          fontSize: 15, 
          color: '#666', 
          marginTop: 8,
          background: '#f5f5f5',
          borderRadius: 8,
          padding: '12px 16px',
          border: '1px solid #e0e0e0',
          maxWidth: 480,
          margin: '0 auto 18px auto'
        }}>
          <strong>What happens next:</strong>
          <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
            <li>Share your thoughts about the game experience</li>
            <li>Complete the demographic questions</li>
            <li>Receive your completion code</li>
            <li>Message the researcher with the code for compensation</li>
          </ul>
        </div>

        {/* Continue button */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button
            onClick={handleContinue}
            style={{
              background: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#388e3c';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#4caf50';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(76, 175, 80, 0.3)';
            }}
          >
            Continue to Post-Game Questions
          </button>
        </div>

        {/* Final note */}
        <div style={{ 
          fontSize: 15, 
          color: '#888', 
          marginTop: 16,
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Your responses help us understand the game design and your discussion thoughts. Thank you!
        </div>
      </div>
    </div>
  );
};

export default GameCompletionPage; 